import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { Box, Typography, Tooltip, Button, TextField, InputAdornment, Alert, IconButton, Menu, MenuItem } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import {
  useVehicleOperations,
  DEFAULT_CO2_RATE_KG_PER_KM,
  CO2_RATE_HELP_TEXT,
} from './useVehicleOperations';
import { downloadActivitiesCSV } from './activitiesCsv';
import { downloadManifestXLSX } from './vehicleManifestXlsx';
import { downloadSimulationStatsXLSX } from './simulationStatsXlsx';
import { downloadEsteLauderMtpWorkbook } from '../../../../services/esteLauder/mtpXlsxExport';
import type { RideSchema, SimulationActivity, UnmatchedRide } from '../../../../types/simulation';
import type { SimulationRunSummary } from '../../../../types/simulation';
import UnmatchedRideDetails, {
  UNMATCHED_RIDE_TOOLTIP_THEME,
  UnmatchedRideGoogleMapsButton,
  UnmatchedRideIdsIcon,
  unmatchedRideTooltipPopperSx,
} from './UnmatchedRideDetails';
import { parseRequestTimeToMs, unmatchedRideStableKey } from './utils';
import type { ActivityNode, VehicleOperation } from './useVehicleOperations';
import { getActivityLegAddressLabel, getActivityLocationLabel, isRedundantOperationalDuplicate } from './DriverActivityDetailPanel';
import { trackAnalyticsEvent } from '../../../../services/analytics';
import { ANALYTICS_EVENT_NAMES } from '../../../../services/eventTaxonomy';
import { liftangoBrandTokens } from '../../../../theme';
import CostsAndStatisticsModal from './CostsAndStatisticsModal';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../../utils/iconFallbacks';
import { opsColors } from '../../../../theme/opsTokens';

const TARMAC = liftangoBrandTokens.visualization.neutralNode;
// Intentional non-theme colors: pickup/dropoff nodes encode simulation event type.
const ORANGE = liftangoBrandTokens.visualization.dropoffNode;
const GREEN = liftangoBrandTokens.visualization.pickupNode;
const FONT_FAMILY = '"Roboto", sans-serif';
const ROW_HEIGHT = 40;
const TRACK_HEIGHT = 2;
const NODE_SIZE = 26;
const ICON_SIZE = 16;
const CANVAS_MIN_WIDTH = 720;
const CANVAS_MAX_WIDTH = 6000; // prevent browser freeze from anomalous totalDurationMs
const MAX_TIME_LABELS = 200;   // cap time-axis DOM elements regardless of sim duration
const TIME_LABEL_HEIGHT = 24;
const STACK_THRESHOLD_MS = 3 * 60 * 1000; // group within 3 minutes
const UNALLOCABLE_ROW_BG = '#FFF9C4';
const UNALLOCABLE_ICON = '#795548';
const MIN_VALID_MS = new Date('2000-01-01T00:00:00Z').getTime();
const MAX_VALID_MS = new Date('2100-01-01T00:00:00Z').getTime();

function validUnmatchedTimeMs(ride: UnmatchedRide): number | null {
  const ms = parseRequestTimeToMs(
    typeof ride.request_time === 'string' ? ride.request_time : undefined
  );
  if (ms == null || ms < MIN_VALID_MS || ms > MAX_VALID_MS) return null;
  return ms;
}

/** Format timeMs to UTC HH:mm for tooltip row labels */
function formatNodeTime(timeMs: number): string {
  const d = new Date(timeMs);
  return `${d.getUTCHours().toString().padStart(2, '0')}:${d.getUTCMinutes().toString().padStart(2, '0')}`;
}

/** Human-readable type label for a node (for stacked tooltip rows) */
function getNodeTypeLabel(type: ActivityNode['type'], t: TFunction<'simulation'>): string {
  switch (type) {
    case 'SHIFT_START': return t('results.operations.nodeTypes.shiftStart');
    case 'SHIFT_END': return t('results.operations.nodeTypes.shiftEnd');
    case 'PICK': return t('results.operations.nodeTypes.pickup');
    case 'DROP': return t('results.operations.nodeTypes.dropoff');
    case 'OTHER': return t('results.operations.nodeTypes.stop');
    default: return String(type);
  }
}

function getNodeAddressLabel(node: ActivityNode, t: TFunction<'simulation'>): string {
  if (node.type === 'PICK') return getActivityLegAddressLabel(node.activity, 'pickup', t);
  if (node.type === 'DROP') return getActivityLegAddressLabel(node.activity, 'dropoff', t);
  return getActivityLocationLabel(node.activity, t);
}

export interface OperationsViewProps {
  activities: SimulationActivity[] | null | undefined;
  ridePoints?: RideSchema[] | null;
  /** Unallocated trips from GET /results/{id}/unmatched_rides (timeline row). */
  unmatchedRides?: UnmatchedRide[] | null;
  /** Run ID for CSV filename (simulation_activities_{runId}.csv) */
  runId?: string | null;
  /** Selected simulation summary from results API for stats export. */
  statsSummary?: SimulationRunSummary | null;
  /** When provided, clicking an activity in the driver detail panel highlights it on the map and centers the map (page variant only) */
  onHighlightActivity?: (activity: SimulationActivity | null) => void;
  /** When provided (e.g. page variant), clicking a vehicle opens the detail panel at page level (e.g. right-side) */
  onVehicleSelectForDetailPanel?: (vehicle: VehicleOperation | null) => void;
  vehicleVisibilityMap?: Record<string, boolean>;
  onVehicleVisibilityChange?: (vehicleId: string, visible: boolean) => void;
  onShowAllVehicles?: () => void;
  onHideAllVehicles?: () => void;
  /** Map-focused unallocated ride key (highlights timeline marker). */
  selectedUnmatchedRideKey?: string | null;
  /** Select an unallocated ride on the full-page map. */
  onSelectUnmatchedRide?: (ride: UnmatchedRide) => void;
  /** When false, hide unallocated timeline markers and map layers; axis excludes those times. */
  unallocatedVisible?: boolean;
  /** Toggles unallocated visibility from the Operations row. */
  onUnallocatedVisibleChange?: (visible: boolean) => void;
  selectedSim?: SimulationRunSummary | null;
  simulationOptions?: SimulationRunSummary[];
}

/** Group nodes that fall within the same time bucket for stacked display */
function groupNodes(nodes: ActivityNode[], thresholdMs: number): ActivityNode[][] {
  if (nodes.length === 0) return [];
  const sorted = [...nodes].sort((a, b) => a.timeMs - b.timeMs);
  const groups: ActivityNode[][] = [];
  let current: ActivityNode[] = [sorted[0]];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].timeMs - current[0].timeMs <= thresholdMs) {
      current.push(sorted[i]);
    } else {
      groups.push(current);
      current = [sorted[i]];
    }
  }
  groups.push(current);
  return groups;
}

/** Stack unmatched markers that fall within the same time bucket (same as vehicle nodes). */
function groupUnmatchedByTime(
  items: { ride: UnmatchedRide; timeMs: number }[],
  thresholdMs: number
): { ride: UnmatchedRide; timeMs: number }[][] {
  if (items.length === 0) return [];
  const sorted = [...items].sort((a, b) => a.timeMs - b.timeMs);
  const groups: { ride: UnmatchedRide; timeMs: number }[][] = [];
  let current: { ride: UnmatchedRide; timeMs: number }[] = [sorted[0]];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].timeMs - current[0].timeMs <= thresholdMs) {
      current.push(sorted[i]);
    } else {
      groups.push(current);
      current = [sorted[i]];
    }
  }
  groups.push(current);
  return groups;
}

const OperationsView: React.FC<OperationsViewProps> = ({
  activities,
  ridePoints = null,
  unmatchedRides,
  runId,
  statsSummary,
  onHighlightActivity,
  onVehicleSelectForDetailPanel,
  vehicleVisibilityMap,
  onVehicleVisibilityChange,
  onShowAllVehicles,
  onHideAllVehicles,
  selectedUnmatchedRideKey = null,
  onSelectUnmatchedRide,
  unallocatedVisible = true,
  onUnallocatedVisibleChange,
  selectedSim = null,
  simulationOptions = [],
}) => {
  const { t } = useTranslation('simulation');
  const [search, setSearch] = useState('');
  const [actionsAnchorEl, setActionsAnchorEl] = useState<HTMLElement | null>(null);
  const [showCo2, setShowCo2] = useState(false);
  const [isCostCalculatorOpen, setIsCostCalculatorOpen] = useState(false);
  const [co2RateInput, setCo2RateInput] = useState(
    DEFAULT_CO2_RATE_KG_PER_KM.toString()
  );
  const parsedCo2Rate = useMemo(() => Number(co2RateInput), [co2RateInput]);
  const {
    vehicles,
    simStartMs,
    simEndMs,
    fleetTotalDistanceKm,
    fleetTotalEmissionsKg,
    co2RateKgPerKm,
    isCo2RateValid,
    co2RateError,
    missingCoordinateSegments,
    processingError,
  } = useVehicleOperations(activities, { co2RateKgPerKm: parsedCo2Rate });

  const filteredVehicles = useMemo(() => {
    if (!search.trim()) return vehicles;
    const q = search.trim().toLowerCase();
    return vehicles.filter(
      (v) =>
        v.displayName.toLowerCase().includes(q) ||
        v.vehicleId.toLowerCase().includes(q)
    );
  }, [vehicles, search]);

  // Debounced vehicle search tracking: emit once 500ms after the user stops typing
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!search.trim()) return;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.vehicle_search, {
        query_length: search.trim().length,
        result_count: filteredVehicles.length,
        timestamp: new Date().toISOString(),
      });
    }, 500);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [search, filteredVehicles.length]);

  const handleShowAll = useCallback(() => {
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.vehicle_visibility_changed, {
      action: 'show_all',
      vehicle_count: vehicles.length,
      timestamp: new Date().toISOString(),
    });
    onShowAllVehicles?.();
    onUnallocatedVisibleChange?.(true);
  }, [vehicles.length, onShowAllVehicles, onUnallocatedVisibleChange]);

  const handleHideAll = useCallback(() => {
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.vehicle_visibility_changed, {
      action: 'hide_all',
      vehicle_count: vehicles.length,
      timestamp: new Date().toISOString(),
    });
    onHideAllVehicles?.();
    onUnallocatedVisibleChange?.(false);
  }, [vehicles.length, onHideAllVehicles, onUnallocatedVisibleChange]);

  const unmatchedWithTimes = useMemo(
    () =>
      (unmatchedRides ?? [])
        .map((ride) => ({ ride, timeMs: validUnmatchedTimeMs(ride) }))
        .filter((x): x is { ride: UnmatchedRide; timeMs: number } => x.timeMs != null),
    [unmatchedRides]
  );

  const hasUnmatchedRidesData = unmatchedWithTimes.length > 0;
  const showUnmatchedTimeline = unallocatedVisible && hasUnmatchedRidesData;

  const displayStartMs = useMemo(() => {
    const uTimes = unallocatedVisible ? unmatchedWithTimes.map((x) => x.timeMs) : [];
    const vStart = vehicles.length > 0 && simStartMs > 0 ? simStartMs : null;
    const candidates = [...uTimes, ...(vStart != null ? [vStart] : [])];
    if (candidates.length === 0) return 0;
    return Math.min(...candidates);
  }, [vehicles.length, simStartMs, unmatchedWithTimes, unallocatedVisible]);

  const displayEndMs = useMemo(() => {
    const uTimes = unallocatedVisible ? unmatchedWithTimes.map((x) => x.timeMs) : [];
    const vEnd = vehicles.length > 0 && simEndMs > 0 ? simEndMs : null;
    const candidates = [...uTimes, ...(vEnd != null ? [vEnd] : [])];
    if (candidates.length === 0) return 0;
    return Math.max(...candidates);
  }, [vehicles.length, simEndMs, unmatchedWithTimes, unallocatedVisible]);

  const rawDurationMs =
    displayEndMs > displayStartMs ? displayEndMs - displayStartMs : 0;
  /** When all events share one instant, pad axis so markers are not at 0% width. */
  const padHalfMs = 30 * 60 * 1000;
  const axisStartMs =
    rawDurationMs <= 0 && (displayStartMs > 0 || displayEndMs > 0)
      ? (displayStartMs || displayEndMs) - padHalfMs
      : displayStartMs;
  const axisEndMs =
    rawDurationMs <= 0 && (displayStartMs > 0 || displayEndMs > 0)
      ? (displayStartMs || displayEndMs) + padHalfMs
      : displayEndMs;
  const axisDurationMs = Math.max(axisEndMs - axisStartMs, 1);

  const handleDownloadCsv = () => {
    const start = Date.now();
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.export_start, {
      export_type: 'simulation_activities_csv',
      export_source: 'simulation_results',
      item_count: activities?.length || 0,
      timestamp: new Date().toISOString(),
    });
    if (activities && activities.length > 0) {
      try {
        downloadActivitiesCSV(activities, runId);
        trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.export_complete, {
          export_type: 'simulation_activities_csv',
          export_source: 'simulation_results',
          item_count: activities.length,
          duration_ms: Date.now() - start,
          timestamp: new Date().toISOString(),
        });
      } catch (error) {
        trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.export_fail, {
          feature_area: 'export',
          error_category: 'export_error',
          error_code: error instanceof Error ? error.name : 'unknown_error',
          retryable: true,
          export_type: 'simulation_activities_csv',
          export_source: 'simulation_results',
          item_count: activities.length,
          duration_ms: Date.now() - start,
          timestamp: new Date().toISOString(),
        });
      }
    }
  };

  const handleDownloadManifest = () => {
    const start = Date.now();
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.export_start, {
      export_type: 'simulation_manifest_xlsx',
      export_source: 'simulation_results',
      item_count: activities?.length || 0,
      timestamp: new Date().toISOString(),
    });
    if (activities && activities.length > 0) {
      try {
        downloadManifestXLSX(activities, runId);
        trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.export_complete, {
          export_type: 'simulation_manifest_xlsx',
          export_source: 'simulation_results',
          item_count: activities.length,
          duration_ms: Date.now() - start,
          timestamp: new Date().toISOString(),
        });
      } catch (error) {
        trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.export_fail, {
          feature_area: 'export',
          error_category: 'export_error',
          error_code: error instanceof Error ? error.name : 'unknown_error',
          retryable: true,
          export_type: 'simulation_manifest_xlsx',
          export_source: 'simulation_results',
          item_count: activities.length,
          duration_ms: Date.now() - start,
          timestamp: new Date().toISOString(),
        });
      }
    }
  };

  const showEmptyFilter = search.trim() && filteredVehicles.length === 0;
  const handleCo2RateBlur = () => {
    if (!isCo2RateValid) {
      setCo2RateInput(DEFAULT_CO2_RATE_KG_PER_KM.toString());
      return;
    }
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.operations_co2_rate_updated, {
      rate_kg_per_km: co2RateKgPerKm,
      source: 'input',
      timestamp: new Date().toISOString(),
    });
  };

  const handleResetCo2Rate = () => {
    setCo2RateInput(DEFAULT_CO2_RATE_KG_PER_KM.toString());
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.operations_co2_rate_updated, {
      rate_kg_per_km: DEFAULT_CO2_RATE_KG_PER_KM,
      source: 'reset',
      timestamp: new Date().toISOString(),
    });
  };
  const isActionsMenuOpen = actionsAnchorEl != null;

  const handleOpenActionsMenu = (event: React.MouseEvent<HTMLElement>) => {
    setActionsAnchorEl(event.currentTarget);
  };

  const handleCloseActionsMenu = () => {
    setActionsAnchorEl(null);
  };

  const handleDownloadCsvFromMenu = () => {
    handleCloseActionsMenu();
    handleDownloadCsv();
  };

  const handleDownloadManifestFromMenu = () => {
    handleCloseActionsMenu();
    handleDownloadManifest();
  };

  const handleDownloadElcMtp = () => {
    const start = Date.now();
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.export_start, {
      export_type: 'elc_mtp_xlsx',
      export_source: 'simulation_results',
      item_count: (activities?.length ?? 0) + (unmatchedRides?.length ?? 0),
      timestamp: new Date().toISOString(),
    });
    try {
      downloadEsteLauderMtpWorkbook({
        activities,
        ridePoints,
        unmatchedRides,
        runId,
      });
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.export_complete, {
        export_type: 'elc_mtp_xlsx',
        export_source: 'simulation_results',
        item_count: (activities?.length ?? 0) + (unmatchedRides?.length ?? 0),
        duration_ms: Date.now() - start,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.export_fail, {
        feature_area: 'export',
        error_category: 'export_error',
        error_code: error instanceof Error ? error.name : 'unknown_error',
        retryable: true,
        export_type: 'elc_mtp_xlsx',
        export_source: 'simulation_results',
        item_count: (activities?.length ?? 0) + (unmatchedRides?.length ?? 0),
        duration_ms: Date.now() - start,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleDownloadElcMtpFromMenu = () => {
    handleCloseActionsMenu();
    handleDownloadElcMtp();
  };

  const handleDownloadStats = () => {
    const start = Date.now();
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.export_start, {
      export_type: 'simulation_stats_xlsx',
      export_source: 'simulation_results',
      item_count: 1,
      timestamp: new Date().toISOString(),
    });
    if (statsSummary) {
      try {
        downloadSimulationStatsXLSX(statsSummary, runId);
        trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.export_complete, {
          export_type: 'simulation_stats_xlsx',
          export_source: 'simulation_results',
          item_count: 1,
          duration_ms: Date.now() - start,
          timestamp: new Date().toISOString(),
        });
      } catch (error) {
        trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.export_fail, {
          feature_area: 'export',
          error_category: 'export_error',
          error_code: error instanceof Error ? error.name : 'unknown_error',
          retryable: true,
          export_type: 'simulation_stats_xlsx',
          export_source: 'simulation_results',
          item_count: 1,
          duration_ms: Date.now() - start,
          timestamp: new Date().toISOString(),
        });
      }
    }
  };

  const handleDownloadStatsFromMenu = () => {
    handleCloseActionsMenu();
    handleDownloadStats();
  };

  const handleTimelineNodeSelect = useCallback(
    (_vehicle: VehicleOperation, node: ActivityNode) => {
      onHighlightActivity?.(node.activity);
    },
    [onHighlightActivity]
  );

  if (vehicles.length === 0 && !hasUnmatchedRidesData) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.paper',
          fontFamily: FONT_FAMILY,
          px: 2,
        }}
      >
        {processingError ? (
          <Alert severity="error" sx={{ width: '100%', maxWidth: 560 }}>
            {t('results.operations.renderError', { message: processingError })}
          </Alert>
        ) : (
          <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
            {t('results.operations.noActivityData')}
          </Typography>
        )}
      </Box>
    );
  }

  const canvasWidth = Math.min(
    CANVAS_MAX_WIDTH,
    Math.max(CANVAS_MIN_WIDTH, (axisDurationMs / (1000 * 60 * 60)) * 80)
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        minHeight: 0,
        height: '100%',
        width: '100%',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        fontFamily: FONT_FAMILY,
        overflow: 'hidden',
      }}
    >
      {/* Header: Search + Download actions */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1.5,
          px: 1.5,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder={t('results.operations.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon size={18} name="magnifying-glass" style={{ color: '#52565e' }} />
                </InputAdornment>
              ),
              sx: {
                fontFamily: FONT_FAMILY,
                fontSize: '0.875rem',
                backgroundColor: 'background.default',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'text.secondary' },
              },
            }}
            sx={{ width: 220, minWidth: 160 }}
          />
          <Tooltip title={showCo2 ? t('results.operations.hideCo2Tooltip') : t('results.operations.showCo2Tooltip')} arrow>
            <IconButton
              size="small"
              onClick={() => setShowCo2((prev) => !prev)}
              aria-label={showCo2 ? t('results.operations.hideCo2Aria') : t('results.operations.showCo2Aria')}
              sx={{
                color: showCo2 ? 'success.main' : 'text.secondary',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              <Icon size={22} d={ICON_FALLBACKS.EnergySavingsLeaf} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('results.operations.openCostCalculator')} arrow>
            <IconButton
              size="small"
              onClick={() => {
                trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.simulation_results_modal_opened, {
                  modal: 'cost_calculator',
                  timestamp: new Date().toISOString(),
                });
                setIsCostCalculatorOpen(true);
              }}
              aria-label={t('results.operations.openCostCalculator')}
              sx={{
                color: 'text.secondary',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              <Icon size={22} d={ICON_FALLBACKS.Calculate} />
            </IconButton>
          </Tooltip>
          {showCo2 && (
            <>
              <Tooltip title={CO2_RATE_HELP_TEXT} arrow>
                <TextField
                  size="small"
                  label={t('results.operations.co2RateLabel')}
                  value={co2RateInput}
                  onChange={(e) => setCo2RateInput(e.target.value)}
                  onBlur={handleCo2RateBlur}
                  error={!isCo2RateValid}
                  helperText={co2RateError}
                  inputProps={{ inputMode: 'decimal' }}
                  sx={{ width: 180 }}
                />
              </Tooltip>
              <Button
                size="small"
                onClick={handleResetCo2Rate}
                sx={{
                  fontFamily: FONT_FAMILY,
                  fontSize: '0.75rem',
                  color: TARMAC,
                  backgroundColor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    backgroundColor: 'background.default',
                    borderColor: 'divider',
                  },
                }}
              >
                {t('results.operations.reset')}
              </Button>
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            size="small"
            onClick={handleShowAll}
            disabled={!onShowAllVehicles}
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: '0.75rem',
              color: TARMAC,
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': {
                backgroundColor: 'background.default',
                borderColor: 'divider',
              },
            }}
          >
            {t('results.operations.showAll')}
          </Button>
          <Button
            size="small"
            onClick={handleHideAll}
            disabled={!onHideAllVehicles}
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: '0.75rem',
              color: TARMAC,
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': {
                backgroundColor: 'background.default',
                borderColor: 'divider',
              },
            }}
          >
            {t('results.operations.hideAll')}
          </Button>
          <Tooltip title={t('results.operations.actions')} arrow>
            <IconButton
              size="small"
              aria-label={t('results.operations.actionsAria')}
              aria-controls={isActionsMenuOpen ? 'operations-actions-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={isActionsMenuOpen ? 'true' : undefined}
              onClick={handleOpenActionsMenu}
              sx={{
                color: TARMAC,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: 'background.default',
                  borderColor: 'divider',
                },
              }}
            >
              <Icon size={20} name="ellipsis-vertical" />
            </IconButton>
          </Tooltip>
            <Menu
            id="operations-actions-menu"
            anchorEl={actionsAnchorEl}
            open={isActionsMenuOpen}
            onClose={handleCloseActionsMenu}
            >
              <MenuItem onClick={handleDownloadManifestFromMenu}>{t('results.operations.downloadManifest')}</MenuItem>
              <MenuItem onClick={handleDownloadCsvFromMenu}>{t('results.operations.downloadActivities')}</MenuItem>
              <MenuItem onClick={handleDownloadStatsFromMenu} disabled={!statsSummary}>{t('results.operations.downloadStats')}</MenuItem>
              <MenuItem
                onClick={handleDownloadElcMtpFromMenu}
                disabled={(activities?.length ?? 0) === 0 && (unmatchedRides?.length ?? 0) === 0}
              >
                {t('results.operations.downloadElcMtp')}
              </MenuItem>
            </Menu>
        </Box>
      </Box>
      {(showCo2 || missingCoordinateSegments > 0) && (
        <Box
          sx={{
            px: 1.5,
            py: 0.75,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 2,
            backgroundColor: 'background.default',
          }}
        >
          {showCo2 && (
            <>
              <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                {t('results.operations.fleetDistanceLabel')} <strong>{t('results.operations.fleetDistanceValue', { value: fleetTotalDistanceKm.toFixed(1) })}</strong>
              </Typography>
              <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                {t('results.operations.fleetCo2Label')} <strong>{t('results.operations.fleetCo2Value', { value: fleetTotalEmissionsKg.toFixed(1) })}</strong>
              </Typography>
            </>
          )}
          {missingCoordinateSegments > 0 && (
            <Typography sx={{ fontSize: '0.75rem', color: 'warning.main' }}>
              {t('results.operations.segmentsSkipped', { count: missingCoordinateSegments })}
            </Typography>
          )}
        </Box>
      )}

      {/* Scrollable body: vehicle list + timeline */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {processingError && (
          <Box sx={{ p: 1.5 }}>
            <Alert severity="error">
              {t('results.operations.renderError', { message: processingError })}
            </Alert>
          </Box>
        )}
        {showEmptyFilter && filteredVehicles.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              py: 2,
              px: 2,
            }}
          >
            <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
              {t('results.operations.noMatchingVehicles', { query: search.trim() })}
            </Typography>
          </Box>
        )}
        {(filteredVehicles.length > 0 || hasUnmatchedRidesData) && (
        <Box
          sx={{
            display: 'flex',
            minHeight:
              TIME_LABEL_HEIGHT +
              filteredVehicles.length * ROW_HEIGHT +
              (hasUnmatchedRidesData ? ROW_HEIGHT : 0),
          }}
        >
      {/* Left column: Vehicle manifest */}
      <Box
        sx={{
          width: 192,
          minWidth: 192,
          borderRight: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
        }}
      >
        <Box sx={{ height: TIME_LABEL_HEIGHT, flexShrink: 0 }} />
        {filteredVehicles.map((v, index) => (
          <Tooltip
            key={v.vehicleId}
            title={
              <Box sx={{ fontFamily: FONT_FAMILY, fontSize: '0.8rem' }}>
                <div>{t('results.operations.vehicleTooltipMaxPax', { value: v.maxPax })}</div>
                <div>{t('results.operations.vehicleTooltipHours', { value: v.activeHours.toFixed(1) })}</div>
                <div>{t('results.operations.vehicleTooltipDistance', { value: v.totalDistanceKm.toFixed(1) })}</div>
                {showCo2 && <div>{t('results.operations.vehicleTooltipCo2', { value: v.emissionsKg.toFixed(1) })}</div>}
                <div>{t('results.operations.vehicleTooltipActivities', { value: v.totalActivities })}</div>
                <div>{t('results.operations.vehicleTooltipClick')}</div>
              </Box>
            }
            placement="right"
            arrow
          >
            <Box
              onClick={() => {
                trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.simulation_results_vehicle_row_selected, {
                  source: 'manifest',
                  timestamp: new Date().toISOString(),
                });
                onVehicleSelectForDetailPanel?.(v);
              }}
              sx={{
                height: ROW_HEIGHT,
                minHeight: ROW_HEIGHT,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'center',
                px: 1.5,
                color: TARMAC,
                fontSize: '0.8rem',
                fontWeight: 600,
                cursor: 'pointer',
                borderBottom: '1px solid',
                borderColor: 'divider',
                backgroundColor: index % 2 === 0 ? opsColors.neutral.panel : opsColors.neutral.bg,
                opacity: vehicleVisibilityMap?.[v.vehicleId] === false ? 0.6 : 1,
                '&:hover': { backgroundColor: opsColors.neutral.panelAlt },
              }}
            >
              <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 0.75 }}>
                <Tooltip title={vehicleVisibilityMap?.[v.vehicleId] === false ? t('results.operations.showVehicle', { name: v.displayName }) : t('results.operations.hideVehicle', { name: v.displayName })} arrow>
                  <span>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        const isVisible = vehicleVisibilityMap?.[v.vehicleId] !== false;
                        onVehicleVisibilityChange?.(v.vehicleId, !isVisible);
                      }}
                      aria-label={vehicleVisibilityMap?.[v.vehicleId] === false ? t('results.operations.showVehicle', { name: v.displayName }) : t('results.operations.hideVehicle', { name: v.displayName })}
                    >
                      {vehicleVisibilityMap?.[v.vehicleId] === false ? (
                        <Icon size={18} name="eye-slash" />
                      ) : (
                        <Icon size={18} name="eye" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, lineHeight: 1.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {v.displayName}
                  </Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', lineHeight: 1.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t('results.operations.vehicleSummary', { maxPax: v.maxPax, distance: v.totalDistanceKm.toFixed(1) })}
                    {showCo2 && t('results.operations.vehicleSummaryCo2', { value: v.emissionsKg.toFixed(1) })}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Tooltip>
        ))}
        {hasUnmatchedRidesData && (
          <Box
            sx={{
              height: ROW_HEIGHT,
              minHeight: ROW_HEIGHT,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'center',
              px: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              backgroundColor: UNALLOCABLE_ROW_BG,
              opacity: unallocatedVisible ? 1 : 0.65,
            }}
          >
            <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 0.75 }}>
              {onUnallocatedVisibleChange != null ? (
                <Tooltip
                  title={
                    unallocatedVisible
                      ? t('results.operations.hideUnallocatedTooltip')
                      : t('results.operations.showUnallocatedTooltip')
                  }
                  arrow
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onUnallocatedVisibleChange(!unallocatedVisible);
                      }}
                      aria-label={
                        unallocatedVisible ? t('results.operations.hideUnallocatedAria') : t('results.operations.showUnallocatedAria')
                      }
                    >
                      {unallocatedVisible ? (
                        <Icon size={18} name="eye" />
                      ) : (
                        <Icon size={18} name="eye-slash" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Box sx={{ width: 34, flexShrink: 0 }} aria-hidden />
              )}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.primary' }}>
                  {t('results.operations.unmatchedRidesLabel')}
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                  {t('results.operations.unmatchedNotServed', { count: unmatchedWithTimes.length })}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* Right column: Timeline (scrollable) */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          overflowX: 'auto',
          overflowY: 'hidden',
        }}
      >
        {/* Time axis: half-hourly markers within sim range */}
        <Box
          sx={{
            height: TIME_LABEL_HEIGHT,
            minWidth: canvasWidth,
            position: 'relative',
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'flex-end',
            pb: 0.25,
          }}
        >
          {(() => {
            const labels: { pct: number; label: string; isHour: boolean }[] = [];
            const stepMs = 30 * 60 * 1000; // 30 minutes
            for (
              let ms = Math.ceil(axisStartMs / stepMs) * stepMs;
              ms <= axisEndMs && labels.length < MAX_TIME_LABELS;
              ms += stepMs
            ) {
              const d = new Date(ms);
              const isHour = d.getUTCMinutes() === 0;
              const pct =
                axisDurationMs > 0 ? ((ms - axisStartMs) / axisDurationMs) * 100 : 0;
              const label = `${d.getUTCHours().toString().padStart(2, '0')}:${d.getUTCMinutes().toString().padStart(2, '0')}`;
              labels.push({ pct, label, isHour });
            }
            return labels.map(({ pct, label, isHour }) => (
              <Box
                key={`${pct}-${label}`}
                sx={{
                  position: 'absolute',
                  left: `${pct}%`,
                  transform: 'translateX(-50%)',
                  fontSize: isHour ? '0.7rem' : '0.6rem',
                  color: isHour ? 'text.primary' : 'text.secondary',
                  fontWeight: isHour ? 600 : 500,
                }}
              >
                {label}
              </Box>
            ));
          })()}
        </Box>

        {/* Gantt tracks - banded rows (click to open driver activity panel) */}
        {filteredVehicles.map((v, index) => (
          <Box
            key={v.vehicleId}
            onClick={() => {
              trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.simulation_results_vehicle_row_selected, {
                source: 'gantt',
                timestamp: new Date().toISOString(),
              });
              onVehicleSelectForDetailPanel?.(v);
            }}
            sx={{
              height: ROW_HEIGHT,
              minHeight: ROW_HEIGHT,
              minWidth: canvasWidth,
              position: 'relative',
              borderBottom: '1px solid',
              borderColor: 'divider',
              backgroundColor: index % 2 === 0 ? opsColors.neutral.panel : opsColors.neutral.bg,
              opacity: vehicleVisibilityMap?.[v.vehicleId] === false ? 0.6 : 1,
              cursor: 'pointer',
              '&:hover': { backgroundColor: opsColors.neutral.panelAlt },
            }}
          >
            {/* Track line */}
            {axisDurationMs > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: (ROW_HEIGHT - TRACK_HEIGHT) / 2,
                  left: `${((v.shiftStart - axisStartMs) / axisDurationMs) * 100}%`,
                  width: `${((v.shiftEnd - v.shiftStart) / axisDurationMs) * 100}%`,
                  height: TRACK_HEIGHT,
                  backgroundColor: opsColors.neutral.line,
                  borderRadius: 1,
                }}
              />
            )}

            {/* Activity nodes (grouped into stacks when at same time) */}
            {(() => {
              const timelineNodes = v.nodes.filter(
                (_n, i, arr) => !isRedundantOperationalDuplicate(arr, i)
              );
              const groups = groupNodes(timelineNodes, STACK_THRESHOLD_MS);
              return groups.map((group, gi) => {
                const node = group[0];
                const pct =
                  axisDurationMs > 0
                    ? ((node.timeMs - axisStartMs) / axisDurationMs) * 100
                    : 0;
                const isStack = group.length > 1;
                const isPick = !isStack && node.type === 'PICK';
                const isDrop = !isStack && node.type === 'DROP';
                const isShift = node.type === 'SHIFT_START' || node.type === 'SHIFT_END' || node.type === 'OTHER';
                const bg = isShift ? TARMAC : isStack ? TARMAC : isPick ? GREEN : isDrop ? ORANGE : TARMAC;
                const canSelectTimelineNode = Boolean(onVehicleSelectForDetailPanel || onHighlightActivity);
                const tooltipTitle = isStack
                  ? undefined
                  : t('results.operations.nodeLineLabel', { label: node.label, address: getNodeAddressLabel(node, t) });
                const tooltipContent = isStack ? (
                  <Box sx={{ p: 0, minWidth: 180, borderRadius: 1, overflow: 'hidden', boxShadow: 1 }}>
                    {group.map((n, ni) => {
                      const rowBg =
                        n.type === 'PICK'
                          ? GREEN
                          : n.type === 'DROP'
                            ? ORANGE
                            : TARMAC;
                      return (
                        <Box
                          key={ni}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1.5,
                            py: 0.75,
                            backgroundColor: rowBg,
                            color: '#fff',
                            fontFamily: FONT_FAMILY,
                            fontSize: '0.8125rem',
                            '&:not(:last-child)': { borderBottom: '1px solid rgba(0,0,0,0.12)' },
                          }}
                        >
                          <Typography component="span" sx={{ fontWeight: 700, flexShrink: 0 }}>
                            {formatNodeTime(n.timeMs)}
                          </Typography>
                          <Typography component="span" sx={{ fontWeight: 500 }}>
                            {getNodeTypeLabel(n.type, t)}
                          </Typography>
                          <Typography component="span" sx={{ fontWeight: 400, opacity: 0.9 }}>
                            {getNodeAddressLabel(n, t)}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                ) : tooltipTitle;
                return (
                  <Tooltip
                    key={gi}
                    title={tooltipContent}
                    placement="top"
                    arrow
                    slotProps={{ popper: { sx: { fontFamily: FONT_FAMILY } } } as any}
                  >
                    <Box
                      component={canSelectTimelineNode ? 'button' : 'div'}
                      type={canSelectTimelineNode ? 'button' : undefined}
                      aria-label={
                        canSelectTimelineNode
                          ? t('results.operations.nodeAria', { type: getNodeTypeLabel(node.type, t), time: formatNodeTime(node.timeMs), vehicle: v.displayName })
                          : undefined
                      }
                      onClick={
                        canSelectTimelineNode
                          ? (e: React.MouseEvent) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleTimelineNodeSelect(v, node);
                            }
                          : undefined
                      }
                      sx={{
                        position: 'absolute',
                        left: `${pct}%`,
                        top: (ROW_HEIGHT - NODE_SIZE) / 2,
                        width: NODE_SIZE,
                        height: NODE_SIZE,
                        transform: 'translateX(-50%)',
                        backgroundColor: bg,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: canSelectTimelineNode ? 'pointer' : 'default',
                        border: 'none',
                        padding: 0,
                        boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                        '&:hover': { opacity: 0.9 },
                      }}
                    >
                      {isStack && (
                        <Icon size={20} d={ICON_FALLBACKS.Layers} style={{ fontSize: ICON_SIZE, color: '#fff' }} />
                      )}
                      {!isStack && isPick && (
                        <Icon size={20} d={ICON_FALLBACKS.ArrowUpward} style={{ fontSize: ICON_SIZE, color: '#fff' }} />
                      )}
                      {!isStack && isDrop && (
                        <Icon size={20} name="arrow-down-long" style={{ fontSize: ICON_SIZE, color: '#fff' }} />
                      )}
                      {!isStack && isShift && (
                        <Icon size={20} name="bus" style={{ fontSize: ICON_SIZE, color: '#fff' }} />
                      )}
                    </Box>
                  </Tooltip>
                );
              });
            })()}
          </Box>
        ))}
        {hasUnmatchedRidesData && (
          <Box
            sx={{
              height: ROW_HEIGHT,
              minHeight: ROW_HEIGHT,
              minWidth: canvasWidth,
              position: 'relative',
              borderBottom: '1px solid',
              borderColor: 'divider',
              backgroundColor: UNALLOCABLE_ROW_BG,
              opacity: unallocatedVisible ? 1 : 0.65,
            }}
          >
            {showUnmatchedTimeline &&
              (() => {
              const groups = groupUnmatchedByTime(unmatchedWithTimes, STACK_THRESHOLD_MS);
              return groups.map((group, gi) => {
                const pct =
                  axisDurationMs > 0
                    ? ((group[0].timeMs - axisStartMs) / axisDurationMs) * 100
                    : 0;
                const isStack = group.length > 1;
                const tooltipContent = isStack ? (
                  <Box sx={{ p: 0 }}>
                    {group.map((g, ni) => (
                      <Box
                        key={ni}
                        sx={{
                          borderBottom:
                            ni < group.length - 1 ? `1px solid ${UNMATCHED_RIDE_TOOLTIP_THEME.divider}` : 'none',
                          pb: ni < group.length - 1 ? 1.25 : 0,
                          mb: ni < group.length - 1 ? 1.25 : 0,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 0.75,
                            mb: 0.75,
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: FONT_FAMILY,
                              fontSize: '0.8125rem',
                              fontWeight: 700,
                              color: UNMATCHED_RIDE_TOOLTIP_THEME.heading,
                              lineHeight: 1.35,
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            {t('results.operations.tripGroupLabel', { time: formatNodeTime(g.timeMs), index: ni + 1, total: group.length })}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.125,
                              flexShrink: 0,
                            }}
                          >
                            <UnmatchedRideGoogleMapsButton
                              ride={g.ride}
                              requestTimeUtcLabel={formatNodeTime(g.timeMs)}
                            />
                            <UnmatchedRideIdsIcon
                              ride={g.ride}
                              surface="timelineTooltip"
                              layout="tooltipHeader"
                            />
                          </Box>
                        </Box>
                        <UnmatchedRideDetails
                          ride={g.ride}
                          variant="tooltip"
                          fontFamily={FONT_FAMILY}
                          hideIdentifiersIcon
                        />
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 0.75,
                        mb: 0.75,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: FONT_FAMILY,
                          fontSize: '0.8125rem',
                          fontWeight: 700,
                          color: UNMATCHED_RIDE_TOOLTIP_THEME.heading,
                          lineHeight: 1.35,
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        {formatNodeTime(group[0].timeMs)}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.125,
                          flexShrink: 0,
                        }}
                      >
                        <UnmatchedRideGoogleMapsButton
                          ride={group[0].ride}
                          requestTimeUtcLabel={formatNodeTime(group[0].timeMs)}
                        />
                        <UnmatchedRideIdsIcon
                          ride={group[0].ride}
                          surface="timelineTooltip"
                          layout="tooltipHeader"
                        />
                      </Box>
                    </Box>
                    <UnmatchedRideDetails
                      ride={group[0].ride}
                      variant="tooltip"
                      fontFamily={FONT_FAMILY}
                      hideIdentifiersIcon
                    />
                  </Box>
                );
                const primaryRide = group[0].ride;
                const isMapSelected =
                  selectedUnmatchedRideKey != null &&
                  group.some((g) => unmatchedRideStableKey(g.ride) === selectedUnmatchedRideKey);
                return (
                  <Tooltip
                    key={gi}
                    title={tooltipContent}
                    placement="top"
                    arrow
                    enterDelay={60}
                    leaveDelay={350}
                    disableInteractive={false}
                    slotProps={
                      {
                        popper: {
                          sx: unmatchedRideTooltipPopperSx,
                        },
                      } as any
                    }
                  >
                    <Box
                      component={onSelectUnmatchedRide ? 'button' : 'div'}
                      type={onSelectUnmatchedRide ? 'button' : undefined}
                      aria-label={onSelectUnmatchedRide ? t('results.operations.unallocatedTripAria') : undefined}
                      onClick={
                        onSelectUnmatchedRide
                          ? (e: React.MouseEvent) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onSelectUnmatchedRide(primaryRide);
                            }
                          : undefined
                      }
                      sx={{
                        position: 'absolute',
                        left: `${pct}%`,
                        top: (ROW_HEIGHT - (NODE_SIZE + 12)) / 2,
                        width: NODE_SIZE + 12,
                        height: NODE_SIZE + 12,
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: onSelectUnmatchedRide ? 'pointer' : 'default',
                        border: 'none',
                        padding: 0,
                        backgroundColor: isMapSelected
                          ? 'rgba(121, 85, 72, 0.18)'
                          : 'transparent',
                        borderRadius: '50%',
                        outline: isMapSelected ? `2px solid ${UNALLOCABLE_ICON}` : 'none',
                        outlineOffset: 2,
                        boxShadow: isMapSelected
                          ? `0 0 0 2px #171a1f`
                          : 'none',
                        '&:hover': {
                          backgroundColor: 'rgba(121, 85, 72, 0.14)',
                        },
                        transition: 'background-color 120ms ease',
                      }}
                    >
                      <Box
                        sx={{
                          position: 'relative',
                          width: NODE_SIZE,
                          height: NODE_SIZE,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'transparent',
                          boxShadow: 'none',
                          pointerEvents: 'none',
                        }}
                      >
                        <Icon size={24} name="triangle-exclamation" style={{ color: UNALLOCABLE_ICON }} />
                        {isStack && (
                          <Icon size={20} d={ICON_FALLBACKS.Layers} style={{
                              fontSize: 12,
                              color: UNALLOCABLE_ICON,
                              position: 'absolute',
                              right: -4,
                              bottom: -2,
                              pointerEvents: 'none',
                              opacity: 0.95,
                            }} />
                        )}
                      </Box>
                    </Box>
                  </Tooltip>
                );
              });
            })()}
          </Box>
        )}
      </Box>
        </Box>
        )}
      </Box>
      <CostsAndStatisticsModal
        open={isCostCalculatorOpen}
        onClose={() => setIsCostCalculatorOpen(false)}
        selectedSim={selectedSim}
        runId={runId}
        simulationOptions={simulationOptions}
      />
    </Box>
  );
};

export default OperationsView;
